/**
 * A trava do perfil normativo no documento da mesa (E222.F1, owner, 04/11, GT-2026-0091).
 *
 * O exame sem perfil normativo não se monta (ADR-0104 §3.6), e a mesa dizia só que "o documento
 * ainda não pode ser montado". Owner: "não adianta termos uma trava que fica travada pra sempre
 * porque o perito nunca vai saber onde corrige". Agora cada pendência tem a frase do que falta e,
 * quando há caminho, o botão que abre o painel do dado no ponto certo: a modalidade da operação
 * (`ModalidadeNoPainel`) ou a data do ato (o instante da assinatura, `InstanteNoPainel`).
 *
 * Com a modalidade já informada e a avaliação ainda sem ela, a mesa diz que o exame está sendo
 * refeito e relê o documento sozinha a cada 15 s (e no "Atualizar").
 */
import { useEffect, type MouseEvent } from 'react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { PendenciaNormativaData, TravaDoPerfilData } from '@/lib/correcaoDoRascunho';

import { useAbrirDado } from './pedidoDoDado';

const t = ptBR.documentoDaMesa.trava;
const modalidades = ptBR.painelDoDado.modalidade.modalidades;
const RELEITURA_MS = 15_000;

type Caminho = 'modalidade' | 'instante' | null;

interface Linha {
  frase: string;
  caminho: Caminho;
  botao: string;
}

/** A frase de cada pendência e o caminho que a mesa oferece para ela. */
export function linhaDaPendencia(p: PendenciaNormativaData, trava: TravaDoPerfilData): Linha {
  const informada = trava.modalidade_informada;
  switch (p.codigo) {
    case 'modalidade_indeterminada':
      return informada === null
        ? {
            frase: t.pendencias.modalidade_indeterminada,
            caminho: 'modalidade',
            botao: t.informarModalidade,
          }
        : {
            frase: t.refazendo.replace('{modalidade}', modalidades[informada]),
            caminho: 'modalidade',
            botao: t.corrigirModalidade,
          };
    case 'modalidade_sem_perfil':
      return informada === 'outra'
        ? { frase: t.pendencias.outraInformada, caminho: 'modalidade', botao: t.corrigirModalidade }
        : { frase: t.pendencias.modalidade_sem_perfil, caminho: null, botao: '' };
    case 'sem_data_do_ato':
      return trava.com_protocolo
        ? { frase: t.pendencias.sem_data_do_ato, caminho: 'instante', botao: t.informarData }
        : { frase: t.pendencias.semDataSemProtocolo, caminho: null, botao: '' };
    case 'data_entre_perfis':
      return { frase: t.pendencias.data_entre_perfis, caminho: 'instante', botao: t.informarData };
    case 'sem_perfil_na_data':
      return {
        frase: t.pendencias.sem_perfil_na_data.replace('{data}', p.detalhe),
        caminho: trava.com_protocolo ? 'instante' : null,
        botao: t.corrigirData,
      };
    default:
      return { frase: t.pendencias.outras, caminho: null, botao: '' };
  }
}

/** A modalidade informada que a avaliação vigente ainda não usou: o exame está sendo refeito. */
export function refazendo(trava: TravaDoPerfilData): boolean {
  return (
    trava.modalidade_informada !== null &&
    trava.pendencias.some((p) => p.codigo === 'modalidade_indeterminada')
  );
}

export function TravaDoPerfil({
  trava,
  onAtualizar,
}: {
  trava: TravaDoPerfilData;
  onAtualizar: () => void;
}): JSX.Element {
  const abrir = useAbrirDado();
  const emCurso = refazendo(trava);
  useEffect(() => {
    if (!emCurso) return undefined;
    const id = window.setInterval(onAtualizar, RELEITURA_MS);
    return () => window.clearInterval(id);
  }, [emCurso, onAtualizar]);
  const linhas = trava.pendencias.map((p) => linhaDaPendencia(p, trava));
  function abrirO(caminho: Exclude<Caminho, null>, frase: string, e: MouseEvent<HTMLElement>) {
    abrir?.({
      origem: {
        tipo: 'declarado',
        chave: caminho,
        linha: null,
        revisao: null,
        valor: caminho === 'modalidade' ? trava.modalidade_informada : null,
      },
      impresso: frase,
      secao: null,
      elemento: e.currentTarget,
    });
  }
  return (
    <div
      className="space-y-sc-3 border border-warning/30 bg-warning-50 p-sc-4"
      data-testid="trava-do-perfil"
    >
      <p className="text-body font-semibold text-fg">{t.titulo}</p>
      <ul className="space-y-sc-3">
        {(linhas.length > 0
          ? linhas
          : [{ frase: t.pendencias.outras, caminho: null, botao: '' }]
        ).map((linha) => (
          <li key={linha.frase} className="space-y-sc-2" data-testid="trava-do-perfil-pendencia">
            <p className="text-body-sm text-fg">{linha.frase}</p>
            {linha.caminho !== null && abrir !== null ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={(e) => abrirO(linha.caminho as Exclude<Caminho, null>, linha.frase, e)}
                data-testid={`trava-do-perfil-${linha.caminho}`}
              >
                {linha.botao}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {emCurso ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={onAtualizar}
          data-testid="trava-do-perfil-atualizar"
        >
          {t.atualizar}
        </Button>
      ) : null}
    </div>
  );
}
